import { create } from "zustand";
import { persist } from "zustand/middleware";
import { APIs } from "../apis";
import type { FindPlaceCandidate } from "../apis/google";
import type { AvailableService } from "../apis/auth";
import { Tools } from "../utils";

let skipAuthRedirectTo = false;

export function getAuthRedirectTo(location: {
  pathname: string;
  search: string;
}): string | null {
  if (skipAuthRedirectTo) {
    skipAuthRedirectTo = false;
    return null;
  }
  return `${location.pathname}${location.search}`;
}

function toAuthTokenState(
  data: any,
  backup?: { refreshToken?: string },
): Pick<
  AuthState,
  "accessToken" | "refreshToken" | "idToken" | "expiresIn" | "tokenType"
> {
  return {
    accessToken: data?.AccessToken ?? null,
    refreshToken: data?.RefreshToken ?? backup?.refreshToken ?? null,
    idToken: data?.IdToken ?? null,
    expiresIn: data?.ExpiresIn ?? null,
    tokenType: data?.TokenType ?? null,
  };
}
type SystemServiceDescription = {
  features: string[];
  description: {
    free: string;
    paid: string;
  };
  serviceName: string;
};

export enum SystemServiceCode {
  telehealth = "TLC",
  doctorHouseVisit = "DHV",
}

export type SystemService = {
  id: string | number;
  name: string;
  status: string;
  code?: SystemServiceCode | null;
  globalPrice?: number | null;
  description?: SystemServiceDescription | null;
  createdAt: string;
  modifiedAt: string;
  createdById?: string | null;
  modifiedById?: string | null;
};

type SystemLanguage = {
  id: number;
  name: string;
  shortName: string;
  status: string;
};

type OrganizationBillingDetails = {
  id: number;
  city: unknown;
  organizationId: number;
  countryId: number;
  billingAddress: string | null;
  zipCode: string | null;
  state: unknown;
};

type Subscription = {
  id: number;
  code: string;
  name: string;
  type: string;
  quotaExceeded: boolean;
};

type OrganizationSubscription = {
  id: number;
  organizationId: number;
  subscriptionId: number;
  stripeSubscriptionId: string | null;
  stripeSubscriptionScheduleId: string | null;
  createdById: number;
  billingCycle: string;
  status: string;
  paymentStatus: string;
  subscriptionType: string;
  startDate: string;
  validTill: string | null;
  endDate: string | null;
  initialPaymentIntentId: string | null;
  assignedCount: number;
  createdAt: string;
  modifiedAt: string;
  subscription?: Subscription;
};

type OrganizationBranding = {
  id: number;
  organizationId: number;
  primaryColor: string | null;
  secondaryColor: string | null;
  tertiaryColor: string | null;
  extraBranding?: Record<string, unknown>;
};

type Organization = {
  id: number;
  cityId: number | null;
  countryId: number;
  name: string;
  address: string | null;
  phoneNumber: string | null;
  email: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  taxId: string | null;
  stripeCustomerId: string | null;
  postalCode: string | null;
  streetAddress: string | null;
  registeredCity: string | null;
  type: string;
  verificationStatus: string;
  status: string;
  created_at: string;
  modified_at: string;
  contactPersonName: string | null;
  contactPersonFirstName?: string;
  contactPersonLastName?: string;
  contactPersonEmail: string | null;
  contactPersonPhone: string | null;
  billingDetails?: OrganizationBillingDetails[];
  subscriptions?: OrganizationSubscription[];
  branding?: OrganizationBranding | null;
};

type UserOrganization = {
  userId: number;
  organizationId: number;
  employeeId: string | null;
  insuranceProviderName: string | null;
  insurancePolicyNumber: string | null;
  status: string;
  organization?: Organization;
};

enum TpaSubscriptionCode {
  tpaPlus = "tpaPlus",
  tpaPremium = "tpaPremium",
  tpaFree = "tpaFree",
}

export type AuthUser = {
  id: string;
  email?: string;
  name: string;
  firstName: string;
  lastName: string;
  systemServices?: SystemService[];
  systemLanguages?: SystemLanguage[];
  user_organization?: UserOrganization;
  paymentRequired: boolean;
  location?: FindPlaceCandidate | null;
  availableServices?: AvailableService[];
};

export type AuthState = {
  isAuthenticated: boolean | undefined;
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
  expiresIn: number | null;
  tokenType: string | null;
  initialize: () => Promise<void>;
  refreshAuthToken: () => Promise<void>;
  login: (
    email: string,
    params?: any,
  ) => Promise<
    | { session: string; user_id: string; user_status: boolean }
    | { isTCAccepted: boolean }
  >;
  logout: () => void;
  verifyEmail: (
    code: string,
    { session, user_id }: { session: string; user_id: string },
  ) => Promise<void>;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: undefined,
      user: null,
      accessToken: null,
      refreshToken: null,
      idToken: null,
      expiresIn: null,
      tokenType: null,
      initialize: async () => {
        const { accessToken } = get();
        if (!accessToken) {
          set({ isAuthenticated: false, user: null });
          return;
        }
        try {
          const user = await APIs.auth.me();
          const userName = Tools.splitName(user.name);
          user.firstName = userName.firstName;
          user.lastName = userName.lastName;
          const org = user?.user_organization?.organization;
          if (org?.contactPersonName) {
            const contactName = Tools.splitName(org.contactPersonName);
            org.contactPersonFirstName = contactName.firstName;
            org.contactPersonLastName = contactName.lastName;
          }
          const { code, quotaExceeded } =
            user?.user_organization?.organization?.subscriptions?.[0]
              ?.subscription ?? ({} as Subscription);
          user.paymentRequired = !(
            [
              TpaSubscriptionCode.tpaPlus,
              TpaSubscriptionCode.tpaPremium,
            ].includes(code as TpaSubscriptionCode) && !quotaExceeded
          );

          set({ user, isAuthenticated: !!user });
        } catch {
          get().logout();
        }
      },
      login: async (email, params = {}) => {
        const { data } = await APIs.auth.login({ email, ...params });
        return data;
      },
      logout: () => {
        skipAuthRedirectTo = true;
        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: null,
          idToken: null,
          expiresIn: null,
          tokenType: null,
        });
      },
      verifyEmail: async (code, { session, user_id }) => {
        const { data } = await APIs.auth.verifyCode({
          code,
          session,
          userId: user_id,
        });
        set({
          isAuthenticated: undefined,
          user: null,
          ...toAuthTokenState(data),
        });
        await get().initialize();
      },
      refreshAuthToken: async () => {
        const refreshToken = get().refreshToken;
        if (!refreshToken) {
          get().logout();
          throw new Error("No refresh token");
        }
        const { data } = await APIs.auth.refreshToken({ refreshToken });
        set(toAuthTokenState(data, { refreshToken }));
      },
    }),
    {
      name: "authStore",
      partialize: ({
        accessToken,
        refreshToken,
        idToken,
        expiresIn,
        tokenType,
      }) => ({
        accessToken,
        refreshToken,
        idToken,
        expiresIn,
        tokenType,
      }),
    },
  ),
);
